import { type FC } from "react";

import { clsx } from "clsx";

import { PAGE_BUCKETS } from "../billing_palette";
import { PageBucketBars, type PageBucketRow } from "../page_bucket_bars";
import { BillableCompositionSubline } from "./composition_subline";
import { SectionHeading } from "./section_heading";
import type { BillableData } from "./types";

export const BillableSection: FC<{
  data: BillableData;
  primary: boolean;
  // `compact` collapses the section to a single inline line, used by the
  // Records-processed card so the BillableSection stays tight under the
  // PAGES section. `full` (default) is the prominent 3-line form used on
  // the Billing By Project drill-in where the section IS the primary
  // content of the page.
  variant?: "compact" | "full";
}> = ({ data, primary, variant = "full" }) => {
  const headlineSize = primary ? "text-3xl" : "text-lg";
  const headlineUnitSize = primary ? "text-base" : "text-sm";
  const { hasCondensedPages } = data;

  if (variant === "compact") {
    // Header-on-its-own-line form matching LETTERS / PAGES section chrome.
    // PAGES section above already anchors the physical page count, so
    // BillableSection here is tight: heading + one-line body. Divergent
    // projects append the composition subline; equal-case projects show
    // just the headline (no breakdown to add — billable == pages).
    return (
      <div>
        <SectionHeading>Billable</SectionHeading>
        <div className="text-sm text-slate-700">
          {data.totalBillablePages}{" "}
          {hasCondensedPages ? "billable units" : "billable"}
          {hasCondensedPages && (
            <>
              <span className="mx-1.5 text-slate-300">&middot;</span>
              <span className="text-slate-500">
                <BillableCompositionSubline
                  fullPageCount={data.fullPageCount}
                  condensedBillableUnits={data.condensedBillableUnits}
                  condensedPageCount={data.condensedPageCount}
                  multiplier={data.condensedMultiplier}
                />
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  // Bucket bars are optional — only the Billing By Project drill-in passes
  // `buckets` to surface the per-bucket breakdown. Records-processed card
  // never reaches here (it uses the compact branch above). Destructure
  // through a local narrowed binding so TS can carry `buckets`'s
  // not-undefined through the `.map` callback without a non-null assertion.
  const { buckets } = data;
  const rows: PageBucketRow[] | null = buckets
    ? PAGE_BUCKETS.map((b) => ({
        key: b.key,
        label: b.label,
        count: buckets[b.key],
        nonBillable: b.key === "previouslySeen",
        billableUnits: data.billableUnitsBuckets?.[b.key],
        condensedPageCount: data.condensedPageCountBuckets?.[b.key],
        multiplier: data.condensedMultiplier,
      }))
    : null;

  return (
    <div>
      <SectionHeading>Billable</SectionHeading>
      <div className="mb-4 flex items-end gap-6">
        <div>
          <div className="leading-none">
            <strong className={clsx(headlineSize, "text-slate-900")}>
              {data.totalBillablePages}
            </strong>{" "}
            <span className={clsx(headlineUnitSize, "ml-1 text-slate-400")}>
              {hasCondensedPages ? "billable units" : "billable"}
            </span>
          </div>
          {hasCondensedPages && (
            <>
              <div className="mt-1 text-[11px] font-medium text-slate-500">
                <BillableCompositionSubline
                  fullPageCount={data.fullPageCount}
                  condensedBillableUnits={data.condensedBillableUnits}
                  condensedPageCount={data.condensedPageCount}
                  multiplier={data.condensedMultiplier}
                />
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {data.totalPages} pages processed
              </div>
            </>
          )}
        </div>
        {data.nonBillableUnits > 0 && (
          <div className="pb-1 text-xs text-slate-500">
            {data.nonBillableUnits} non-billable (previously seen)
          </div>
        )}
      </div>
      {rows && <PageBucketBars total={data.totalPages} rows={rows} />}
    </div>
  );
};
